import { useMemo } from "react";
import { awardedItems } from "@/mocks/awardedItems";
import { motion } from "motion/react";

export default function AwardedStatsBanner() {
  const stats = useMemo(() => {
    const totalCases = awardedItems.length;
    const totalProfit = awardedItems.reduce((sum, item) => sum + item.profitBreakdown.estimatedProfit, 0);
    const avgWinningRate = awardedItems.reduce((sum, item) => sum + (item.actualWinningBid / item.appraisedValue) * 100, 0) / totalCases;
    const avgProfitRate = awardedItems.reduce((sum, item) => sum + item.profitBreakdown.profitRate, 0) / totalCases;
    const avgBidderCount = awardedItems.reduce((sum, item) => sum + item.bidderCount, 0) / totalCases;
    const recommendedCount = awardedItems.filter((item) => item.expertGrade === "적극추천").length;
    return {
      totalCases,
      totalProfit,
      avgWinningRate: Math.round(avgWinningRate * 10) / 10,
      avgProfitRate: Math.round(avgProfitRate * 10) / 10,
      avgBidderCount: Math.round(avgBidderCount * 10) / 10,
      recommendedCount,
    };
  }, []);

  const kpiCards = [
    {
      icon: "ri-check-double-line",
      label: "분석 물건 낙찰 건수",
      value: `${stats.totalCases}건`,
      sub: `강스터디 적극추천 ${stats.recommendedCount}건`,
      color: "bg-amber-50 text-amber-700",
      iconBg: "bg-amber-100",
    },
    {
      icon: "ri-percent-line",
      label: "평균 낙찰가율",
      value: `${stats.avgWinningRate}%`,
      sub: "감정가 대비 낙찰가 비율",
      color: "bg-emerald-50 text-emerald-700",
      iconBg: "bg-emerald-100",
    },
    {
      icon: "ri-line-chart-line",
      label: "평균 수익률",
      value: `${stats.avgProfitRate}%`,
      sub: "취득세·리모델링 등 비용 차감 후",
      color: "bg-rose-50 text-rose-700",
      iconBg: "bg-rose-100",
    },
    {
      icon: "ri-money-dollar-circle-line",
      label: "총 예상 순수익",
      value: `${(stats.totalProfit / 10000).toLocaleString()}만원`,
      sub: `평균 ${stats.avgBidderCount}명 경쟁`,
      color: "bg-indigo-50 text-indigo-700",
      iconBg: "bg-indigo-100",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
      className="mb-8"
    >
      <div className="flex items-center gap-2 mb-4">
        <i className="ri-trophy-line text-[#C4956A]"></i>
        <span className="text-sm font-semibold text-[#1C1917]">
          최근 강스터디 분석 물건 낙찰 성공 사례
        </span>
        <span className="text-xs text-[#8C8780]">
          · 2025.11 ~ 2026.04 누적
        </span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiCards.map((card, idx) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + idx * 0.05, duration: 0.4 }}
            className="bg-white rounded-xl border border-[#E8E5E0] p-4 hover:border-[#C4956A]/20 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-lg ${card.iconBg} flex items-center justify-center shrink-0`}>
                <i className={`${card.icon} text-lg ${card.color.split(" ")[1]}`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-[#8C8780] mb-0.5 whitespace-nowrap">{card.label}</p>
                <p className="text-xl font-bold text-[#1C1917] tracking-tight">{card.value}</p>
                <p className="text-[11px] text-[#8C8780] mt-0.5 whitespace-nowrap">{card.sub}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}